/* assets/section-tabbed-scenario.js */
(() => {
  'use strict';

  const SECTION_SEL = '.tabbed-scenario';

  function positionHighlight(root) {
    const tabsEl = root.querySelector('.tabbed-scenario__tabs');
    const highlight = root.querySelector('.tabbed-scenario__tab-highlight');
    if (!tabsEl || !highlight) return;

    const active =
      tabsEl.querySelector('.tabbed-scenario__tab.is-active') ||
      tabsEl.querySelector('.tabbed-scenario__tab');
    if (!active) return;

    const left = active.offsetLeft - tabsEl.scrollLeft;
    highlight.style.width = `${active.offsetWidth}px`;
    highlight.style.transform = `translateX(${left}px)`;
  }

  function destroySwipers(root) {
    const instances = root._tabbedScenarioSwipers || [];
    instances.forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    root._tabbedScenarioSwipers = [];
  }

  function initSwiper(panel) {
    const container = panel.querySelector('.tabbed-scenario__swiper');
    if (!container || typeof Swiper === 'undefined') return null;
    if (container.swiper) return container.swiper;

    const slideCount = parseInt(panel.dataset.slideCount || '0', 10)
      || container.querySelectorAll('.swiper-slide').length;
    const showControls = slideCount > 3;
    const prevEl = panel.querySelector('.tabbed-scenario__nav--prev');
    const nextEl = panel.querySelector('.tabbed-scenario__nav--next');
    const paginationEl = panel.querySelector('.tabbed-scenario__progress');

    const config = {
      slidesPerView: 1.2,
      spaceBetween: 16,
      speed: 550,
      grabCursor: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      breakpoints: {
        750: {
          slidesPerView: Math.min(2, slideCount),
          spaceBetween: 20,
        },
        990: {
          slidesPerView: Math.min(3, slideCount),
          spaceBetween: 28,
        },
      },
    };

    if (showControls) {
      if (prevEl && nextEl) {
        config.navigation = { prevEl, nextEl };
      }
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          type: 'progressbar',
        };
      }
    }

    return new Swiper(container, config);
  }

  function ensurePanelSwiper(root, panel) {
    if (!panel) return null;
    const container = panel.querySelector('.tabbed-scenario__swiper');
    if (!container) return null;
    if (container.swiper) {
      container.swiper.update();
      return container.swiper;
    }
    const swiper = initSwiper(panel);
    if (swiper) {
      if (!root._tabbedScenarioSwipers) root._tabbedScenarioSwipers = [];
      root._tabbedScenarioSwipers.push(swiper);
    }
    return swiper;
  }

  function switchTab(root, index) {
    const tabs = root.querySelectorAll('.tabbed-scenario__tab');
    const panels = root.querySelectorAll('.tabbed-scenario__panel');

    tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('is-active', isActive);
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    positionHighlight(root);

    const activePanel = panels[index];
    requestAnimationFrame(() => {
      const swiper = ensurePanelSwiper(root, activePanel);
      if (swiper) swiper.slideTo(0, 0);
    });
  }

  function init(root) {
    if (!root || root.dataset.tabbedScenarioInit === 'true') return;

    const tabs = root.querySelectorAll('.tabbed-scenario__tab');
    const panels = root.querySelectorAll('.tabbed-scenario__panel');

    if (panels.length === 0) return;

    root._tabbedScenarioSwipers = [];

    panels.forEach((panel, index) => {
      const isActive = index === 0;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    ensurePanelSwiper(root, panels[0]);

    requestAnimationFrame(() => positionHighlight(root));

    const onResize = () => positionHighlight(root);
    root._tabbedScenarioOnResize = onResize;
    window.addEventListener('resize', onResize, { passive: true });

    const tabsEl = root.querySelector('.tabbed-scenario__tabs');
    if (tabsEl) {
      const onScroll = () => positionHighlight(root);
      root._tabbedScenarioOnScroll = onScroll;
      tabsEl.addEventListener('scroll', onScroll, { passive: true });
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => switchTab(root, index));

      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + tabs.length) % tabs.length;
        tabs[nextIndex].focus();
        switchTab(root, nextIndex);
      });
    });

    root.dataset.tabbedScenarioInit = 'true';
  }

  function teardown(root) {
    if (!root) return;
    destroySwipers(root);
    if (root._tabbedScenarioOnResize) {
      window.removeEventListener('resize', root._tabbedScenarioOnResize);
      delete root._tabbedScenarioOnResize;
    }
    if (root._tabbedScenarioOnScroll) {
      const tabsEl = root.querySelector('.tabbed-scenario__tabs');
      if (tabsEl) tabsEl.removeEventListener('scroll', root._tabbedScenarioOnScroll);
      delete root._tabbedScenarioOnScroll;
    }
    delete root.dataset.tabbedScenarioInit;
  }

  function initAll() {
    document.querySelectorAll(SECTION_SEL).forEach(init);
  }

  function whenSwiperReady(cb, attempts) {
    const max = attempts || 40;
    if (typeof Swiper !== 'undefined') {
      cb();
      return;
    }
    if (max <= 0) {
      console.warn('tabbed-scenario: Swiper library not loaded');
      cb();
      return;
    }
    setTimeout(() => whenSwiperReady(cb, max - 1), 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => whenSwiperReady(initAll));
  } else {
    whenSwiperReady(initAll);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root =
      event.target.querySelector(SECTION_SEL) ||
      (event.target.matches?.(SECTION_SEL) ? event.target : null);
    if (!root) return;
    teardown(root);
    whenSwiperReady(() => init(root));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root =
      event.target.querySelector(SECTION_SEL) ||
      (event.target.matches?.(SECTION_SEL) ? event.target : null);
    if (root) teardown(root);
  });
})();
